import { useContext } from "react";
import { z } from "zod";

import { ApiFetchFunction, LdoAuthContext } from "@nens/ldo/auth-v1";
import { useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { API_BASE_URL } from "../constants";
import { LdoPaginated, paginatedSchema } from "../pagination";

import { excelImportUploadMultipartZipfile } from "./excel-imports-multipart";

export type Mode = "create" | "update";

const parseResponseSchema = z.object({
  total: z.number(),
  items: z.array(
    z.object({
      id: z.number().nullable(),
      input: z.record(z.string().or(z.number()).or(z.null()).or(z.boolean())),
      meta: z.record(z.string().or(z.number()).or(z.null()).or(z.boolean())).nullable(),
      meta_version: z.number(),
      files: z
        .array(
          z.object({
            type: z.string(),
            path: z.string(),
          })
        )
        .nullable(),
      errors: z.array(
        z.object({
          loc: z.array(z.string()),
          msg: z.string(),
          type: z.string(),
        })
      ),
    })
  ),
});

export type ParseResponse = z.infer<typeof parseResponseSchema>;

const excelImportSchema = z.object({
  id: z.number(),
  mode: z.literal("create").or(z.literal("update")).or(z.literal("migration")),
  created_at: z.string(), // Date
  created_by: z.object({
    id: z.string(), // UUID
    name: z.string(),
  }),
  updated_at: z.string(),
  status: z.string(), // Enum?
  scenario_ids: z.array(z.number()),
  errors: z.array(z.object({ msg: z.string() })),
});

export type ExcelImport = z.infer<typeof excelImportSchema>;

export function useExcelImportsList() {
  const url = combineUrlAndParams(`${API_BASE_URL}/excel-imports`, { order_by: "-id" });
  const { apiFetchFunction, tenantId } = useContext(LdoAuthContext);
  return useParsedQuery<LdoPaginated<ExcelImport>>(
    [tenantId, "ldo-api-v1", "excel-imports"],
    () => apiFetchFunction(url),
    (json: unknown) => paginatedSchema(excelImportSchema).parse(json)
  );
}

export async function excelImportParseFile(
  apiFetchFunction: ApiFetchFunction,
  mode: Mode,
  file: File
) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiFetchFunction(`${API_BASE_URL}/excel-imports/parse?mode=${mode}`, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    method: "POST",
    body: formData,
  });
  return parseResponseSchema.parse(response);
}

export async function excelImportUploadFile(
  apiFetchFunction: ApiFetchFunction,
  mode: Mode,
  file: File
): Promise<ExcelImport> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiFetchFunction(`${API_BASE_URL}/excel-imports?mode=${mode}`, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    method: "POST",
    body: formData,
  });
  return excelImportSchema.parse(response);
}

const MAX_SINGLE_UPLOAD_FILE_SIZE = 100 * 1024 * 1024; // 100MB as recommended by https://docs.aws.amazon.com/AmazonS3/latest/userguide/qfacts.html

export async function excelImportUploadZipfile(
  apiFetchFunction: ApiFetchFunction,
  excelImportId: number,
  file: File
) {
  if (!file.name.toLowerCase().endsWith(".zip"))
    throw new Error("excelImportUploadZipfile should only be used with ZIP files.");

  if (file.size >= MAX_SINGLE_UPLOAD_FILE_SIZE)
    return await excelImportUploadMultipartZipfile(apiFetchFunction, excelImportId, file);

  const formData = new FormData();
  formData.append("file", file);

  // First get an object store URL
  const response = await apiFetchFunction(
    `${API_BASE_URL}/excel-imports/${excelImportId}/files/files.zip/upload`,
    {
      method: "PUT",
    }
  );
  const { url } = z.object({ url: z.string() }).parse(response);

  // Then upload file to that URL
  return fetch(url, { method: "PUT", body: formData, credentials: "omit" });
}
